import { useCallback, useEffect, useState } from "react"
import { Link, useNavigate, useSearchParams } from "react-router-dom"
import { useTranslation } from "react-i18next"
import axios from "axios"
import {
  Plus,
  Filter,
  Sparkles,
  Upload,
  Search,
  Paperclip,
  Link2,
  ClipboardList,
  Clock,
  UserPlus,
} from "lucide-react"
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip } from "recharts"
import AppLayout from "@/components/app/AppLayout"
import { PilotBanner } from "@/components/app/PilotBanner"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { API_BASE } from "@/config/api"
import { formatDate, formatRon } from "@/lib/format"
import {
  readClientTrack,
  readMskFields,
  sectionVisible,
} from "@/config/client-profile-sections"
import { CLINICAL_VERTICALS } from "@/config/clinical-verticals"
import { useClinicalContext } from "@/hooks/use-clinical-context"
import { useEntitlements } from "@/hooks/use-entitlements"
import { toast } from "sonner"

const API = API_BASE

const statusStyle = {
  active: "bg-secondary text-secondary-foreground",
  new: "bg-accent/15 text-accent",
  inactive: "bg-muted text-muted-foreground",
}

const SHEET_TABS = ["activitate", "clinic", "facturare", "comunicari"]

export default function Clients() {
  const { t } = useTranslation()
  const [clients, setClients] = useState([])
  const [query, setQuery] = useState("")
  const [statusFilter, setStatusFilter] = useState("all")
  const [selected, setSelected] = useState(null)
  const [profileForm, setProfileForm] = useState({
    name: "",
    email: "",
    phone: "",
    status: "active",
    tags: "",
    note: "",
  })
  const [timeline, setTimeline] = useState([])
  const [overview, setOverview] = useState(null)
  const [summary, setSummary] = useState(null)
  const [addOpen, setAddOpen] = useState(false)
  const [form, setForm] = useState({ name: "", email: "", phone: "", status: "new", tags: "" })
  const [segments, setSegments] = useState([])
  const [segmentId, setSegmentId] = useState("all")
  const [nlQuery, setNlQuery] = useState("")
  const [nlResults, setNlResults] = useState(null)
  const [assessments, setAssessments] = useState([])
  const [attachments, setAttachments] = useState([])
  const [sentiment, setSentiment] = useState([])
  const [relations, setRelations] = useState([])
  const [sheetTab, setSheetTab] = useState("activitate")
  const [phqOpen, setPhqOpen] = useState(false)
  const [phqScore, setPhqScore] = useState("12")
  const [relationForm, setRelationForm] = useState({ related_client_id: "", relation_type: "family" })
  const [params, setParams] = useSearchParams()
  const navigate = useNavigate()
  const { enabledVerticals, hasPsychTools, hasMskTools } = useClinicalContext()
  const { hasFeature } = useEntitlements()
  const canPatient360 = hasFeature("patient_360")
  const canWaitlist = hasFeature("waitlist")
  const [cnasForm, setCnasForm] = useState({
    cnas_insured_code: "",
    cnas_patient_id: "",
    birth_date: "",
    parafa: "",
    referral_bilet: "",
  })
  const [mskForm, setMskForm] = useState({
    primary_diagnosis: "",
    affected_region: "",
    vas_baseline: "",
    referral_bilet_msk: "",
  })
  const [clientTrack, setClientTrack] = useState("")

  const syncProfileFromClient = useCallback((c) => {
    setProfileForm({
      name: c?.name || "",
      email: c?.email || "",
      phone: c?.phone || "",
      status: c?.status || "active",
      tags: Array.isArray(c?.tags) ? c.tags.join(", ") : "",
      note: c?.note || "",
    })
  }, [])

  const syncCnasFromClient = useCallback((c) => {
    const cf = c?.custom_fields && typeof c.custom_fields === "object" ? c.custom_fields : {}
    setCnasForm({
      cnas_insured_code: String(cf.cnas_insured_code ?? ""),
      cnas_patient_id: String(cf.cnas_patient_id ?? cf.patient_cnas_id ?? ""),
      birth_date: String(cf.birth_date ?? cf.data_nasterii ?? ""),
      parafa: String(cf.parafa ?? cf.physician_parafa ?? ""),
      referral_bilet: String(cf.referral_bilet ?? cf.referral_id ?? ""),
    })
    setMskForm(readMskFields(cf))
    setClientTrack(readClientTrack(cf) || "")
  }, [])

  const openClient = useCallback(async (c, tab) => {
    setSelected(c)
    syncProfileFromClient(c)
    syncCnasFromClient(c)
    setSummary(null)
    setOverview(null)
    if (tab && SHEET_TABS.includes(tab)) setSheetTab(tab)
    else setSheetTab("activitate")
    try {
      const { data } = await axios.get(`${API}/clients/${c.id}/timeline`)
      setTimeline(data.items || [])
    } catch {
      setTimeline([])
    }
    if (canPatient360) {
      axios
        .get(`${API}/patients/${c.id}/360`)
        .then((r) => setOverview(r.data))
        .catch(() => setOverview(null))
    }
    axios.get(`${API}/clients/${c.id}/assessments`).then((r) => setAssessments(r.data)).catch(() => setAssessments([]))
    axios.get(`${API}/clients/${c.id}/attachments`).then((r) => setAttachments(r.data)).catch(() => setAttachments([]))
    axios.get(`${API}/clients/${c.id}/sentiment-trend`).then((r) => setSentiment(r.data.points || [])).catch(() => setSentiment([]))
    axios.get(`${API}/clients/${c.id}/relations`).then((r) => setRelations(r.data.relations || r.data || [])).catch(() => setRelations([]))
  }, [canPatient360, syncCnasFromClient, syncProfileFromClient])

  const load = useCallback(() => {
    axios.get(`${API}/clients`).then((r) => {
      setClients(r.data)
      const openId = params.get("open")
      if (openId) {
        const c = r.data.find((x) => x.id === openId)
        if (c) openClient(c, params.get("tab") || undefined)
      }
    }).catch(() => {})
    axios.get(`${API}/segments`).then((r) => setSegments(r.data)).catch(() => {})
    if (params.get("action") === "new") setAddOpen(true)
  }, [params, openClient])

  useEffect(() => { load() }, [load])

  useEffect(() => {
    if (!selected?.id || !canPatient360 || overview) return
    axios
      .get(`${API}/patients/${selected.id}/360`)
      .then((r) => setOverview(r.data))
      .catch(() => setOverview(null))
  }, [selected?.id, canPatient360, overview])

  const runNlSearch = async () => {
    if (!nlQuery.trim()) return setNlResults(null)
    try {
      const { data } = await axios.post(`${API}/ai/clients/search`, { query: nlQuery })
      setNlResults(data.clients || [])
      toast.success(`${data.clients?.length || 0} ${t("clientsPanel.results")}`)
    } catch {
      toast.error(t("app.clients.toast.searchFailed"))
    }
  }

  const saveProfile = async () => {
    if (!selected) return
    try {
      const { data } = await axios.patch(`${API}/clients/${selected.id}`, {
        name: profileForm.name.trim(),
        email: profileForm.email.trim(),
        phone: profileForm.phone.trim(),
        status: profileForm.status,
        note: profileForm.note,
        tags: profileForm.tags
          ? profileForm.tags.split(",").map((x) => x.trim()).filter(Boolean)
          : [],
      })
      setSelected(data)
      syncProfileFromClient(data)
      setClients((prev) => prev.map((x) => (x.id === data.id ? { ...x, ...data } : x)))
      toast.success(t("clientsPanel.profileSaved"))
    } catch {
      toast.error(t("clientsPanel.saveFailed"))
    }
  }

  const addAssessment = async () => {
    if (!selected) return
    const score = parseInt(phqScore, 10)
    if (Number.isNaN(score) || score < 0 || score > 27) {
      toast.error(t("clientsPanel.phqInvalid"))
      return
    }
    try {
      await axios.post(`${API}/clients/${selected.id}/assessments`, { scale_type: "PHQ-9", score })
      const { data } = await axios.get(`${API}/clients/${selected.id}/assessments`)
      setAssessments(data)
      setPhqOpen(false)
      toast.success(t("app.clients.toast.assessmentAdded"))
    } catch {
      toast.error(t("app.clients.toast.assessmentFailed"))
    }
  }

  const uploadAttachment = async (file) => {
    if (!selected || !file) return
    const fd = new FormData()
    fd.append("file", file)
    try {
      await axios.post(`${API}/clients/${selected.id}/attachments/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      const { data } = await axios.get(`${API}/clients/${selected.id}/attachments`)
      setAttachments(data)
      toast.success(t("clientsPanel.fileUploaded"))
    } catch {
      toast.error(t("clientsPanel.uploadFailed"))
    }
  }

  const issuePortalLink = async () => {
    if (!selected?.id) return
    try {
      const { data } = await axios.post(`${API}/clients/${selected.id}/portal-token`)
      await navigator.clipboard.writeText(data.portal_url || "")
      toast.success(t("clientsPanel.portalCopied"))
    } catch {
      toast.error(t("clientsPanel.portalFailed"))
    }
  }

  const addToWaitlist = async () => {
    if (!selected?.id || !canWaitlist) return
    try {
      await axios.post(`${API}/waitlist`, { client_id: selected.id })
      toast.success(t("clientsPanel.waitlistAdded"))
      navigate("/app/waitlist")
    } catch {
      toast.error(t("clientsPanel.waitlistFailed"))
    }
  }

  const addRelation = async () => {
    if (!selected || !relationForm.related_client_id) return
    try {
      await axios.post(`${API}/clients/${selected.id}/relations`, relationForm)
      const { data } = await axios.get(`${API}/clients/${selected.id}/relations`)
      setRelations(data.relations || data || [])
      setRelationForm({ related_client_id: "", relation_type: "family" })
      toast.success(t("clientsPanel.relationAdded"))
    } catch {
      toast.error(t("clientsPanel.relationFailed"))
    }
  }

  const removeRelation = async (relationId) => {
    if (!selected) return
    try {
      await axios.delete(`${API}/clients/${selected.id}/relations/${relationId}`)
      setRelations((prev) => prev.filter((r) => r.id !== relationId))
      toast.success(t("clientsPanel.relationRemoved"))
    } catch {
      toast.error(t("clientsPanel.relationFailed"))
    }
  }

  const displayList = nlResults ?? clients
  const filtered = displayList.filter((c) => {
    const q = query.toLowerCase()
    const matchQ = !q || c.name.toLowerCase().includes(q) || c.email.toLowerCase().includes(q)
    const matchS = statusFilter === "all" || c.status === statusFilter
    const seg = segments.find((s) => s.id === segmentId)
    const matchSeg = segmentId === "all" || !seg || (seg.filters?.status ? c.status === seg.filters.status : true)
    return matchQ && matchS && matchSeg
  })

  const addClient = async () => {
    try {
      const { data } = await axios.post(`${API}/clients`, {
        ...form,
        tags: form.tags ? form.tags.split(",").map((x) => x.trim()) : [],
      })
      setClients((prev) => [...prev, data].sort((a, b) => a.name.localeCompare(b.name)))
      setAddOpen(false)
      setForm({ name: "", email: "", phone: "", status: "new", tags: "" })
      toast.success(t("clientsPanel.clientAdded"))
    } catch {
      toast.error(t("clientsPanel.addFailed"))
    }
  }

  const importCsv = async (file) => {
    const text = await file.text()
    const lines = text.split("\n").slice(1).filter(Boolean)
    const rows = lines.map((line) => {
      const [name, email, phone] = line.split(",").map((s) => s.trim())
      return { name, email, phone, status: "new" }
    })
    try {
      const { data } = await axios.post(`${API}/clients/import`, rows)
      toast.success(`${data.created} ${t("clientsPanel.imported")}`)
      load()
    } catch {
      toast.error(t("clientsPanel.importFailed"))
    }
  }

  const aiSummary = async () => {
    if (!selected) return
    try {
      const { data } = await axios.post(`${API}/ai/clients/${selected.id}/summary`)
      setSummary(data)
      toast.success(t("clientsPanel.aiSummaryOk"))
    } catch {
      toast.error(t("clientsPanel.aiSummaryFail"))
    }
  }

  const saveCustomFields = async (fields) => {
    if (!selected) return
    try {
      const { data } = await axios.patch(`${API}/clients/${selected.id}`, {
        custom_fields: fields,
      })
      setSelected(data)
      setClients((prev) => prev.map((x) => (x.id === data.id ? { ...x, ...data } : x)))
      syncCnasFromClient(data)
      toast.success(t("clientsPanel.saved"))
    } catch {
      toast.error(t("clientsPanel.saveFailed"))
    }
  }

  const saveCnasPilotFields = () =>
    saveCustomFields({
      cnas_insured_code: cnasForm.cnas_insured_code,
      cnas_patient_id: cnasForm.cnas_patient_id,
      birth_date: cnasForm.birth_date,
      parafa: cnasForm.parafa,
      referral_bilet: cnasForm.referral_bilet,
    })

  const saveMskFields = () =>
    saveCustomFields({
      primary_diagnosis: mskForm.primary_diagnosis,
      affected_region: mskForm.affected_region,
      vas_baseline: mskForm.vas_baseline,
      referral_bilet_msk: mskForm.referral_bilet_msk,
    })

  const saveClientTrack = () =>
    saveCustomFields({ primary_vertical: clientTrack || null })

  const showCnas = sectionVisible({ verticals: ["physio", "kineto", "psych", "speech", "nutrition"] }, enabledVerticals, clientTrack)
  const showPsych = hasPsychTools && sectionVisible({ verticals: ["psych"] }, enabledVerticals, clientTrack)
  const showMsk = hasMskTools && sectionVisible({ verticals: ["physio", "kineto"] }, enabledVerticals, clientTrack)

  const hasReferralBilet = Boolean(
    (cnasForm.referral_bilet || "").trim() || (mskForm.referral_bilet_msk || "").trim()
  )

  const glance = (() => {
    if (!overview) return null
    const appts = overview.appointments || []
    const notes = overview.notes || []
    const plans = overview.care_plans || []
    const invs = overview.invoices || []
    const unpaid = invs.filter((i) => ["unpaid", "overdue", "sent"].includes(String(i.status || "").toLowerCase()))
    const activePlan = plans.find((p) => ["active", "in_progress", "signed"].includes(String(p.status || "").toLowerCase())) || plans[0]
    return {
      lastAppt: appts[0],
      notesCount: notes.length,
      draftNotes: notes.filter((n) => n.status === "draft").length,
      activePlan,
      unpaidCount: unpaid.length,
    }
  })()

  const timelineHref = (item) => {
    if (!selected) return null
    if (item.type === "appointment") return `/app/appointments?client=${selected.id}`
    if (item.type === "note") return `/app/notes?client=${selected.id}`
    if (item.type === "invoice") return "/app/billing"
    return null
  }

  const closeSheet = () => {
    setSelected(null)
    if (params.get("open") || params.get("tab")) {
      const next = new URLSearchParams(params)
      next.delete("open")
      next.delete("tab")
      setParams(next, { replace: true })
    }
  }

  const otherClients = clients.filter((c) => c.id !== selected?.id)

  return (
    <AppLayout
      title={t("clientsPanel.pageTitle")}
      actions={
        <>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-32 h-9 rounded-full" data-testid="clients-filter">
              <Filter strokeWidth={1.5} className="h-4 w-4 mr-1" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("clientsPanel.filterAll")}</SelectItem>
              <SelectItem value="active">{t("clientsPanel.filterActive")}</SelectItem>
              <SelectItem value="new">{t("clientsPanel.filterNew")}</SelectItem>
              <SelectItem value="inactive">{t("clientsPanel.filterInactive")}</SelectItem>
            </SelectContent>
          </Select>
          <label className="cursor-pointer">
            <input type="file" accept=".csv" className="hidden" onChange={(e) => e.target.files?.[0] && importCsv(e.target.files[0])} />
            <span className="inline-flex items-center text-sm px-3 py-1.5 rounded-full border border-border hover:bg-muted">
              <Upload className="h-4 w-4 mr-1" /> {t("clientsPanel.importCsv")}
            </span>
          </label>
          <Button size="sm" className="rounded-full" data-testid="clients-add" onClick={() => setAddOpen(true)}>
            <Plus strokeWidth={1.5} className="h-4 w-4 mr-1" /> {t("clientsPanel.addClient")}
          </Button>
        </>
      }
    >
      <div className="mb-5 flex flex-wrap items-center gap-3">
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t("clientsPanel.searchPlaceholder")}
          className="max-w-sm rounded-md"
          data-testid="clients-search"
        />
        <div className="flex gap-2 max-w-md flex-1">
          <Input value={nlQuery} onChange={(e) => setNlQuery(e.target.value)} placeholder={t("clientsPanel.nlPlaceholder")} className="rounded-md" />
          <Button variant="outline" size="sm" className="rounded-full shrink-0" onClick={runNlSearch}>
            <Search className="h-4 w-4" />
          </Button>
        </div>
        {segments.length > 0 && (
          <Select value={segmentId} onValueChange={setSegmentId}>
            <SelectTrigger className="w-36 h-9 rounded-full"><SelectValue placeholder="Segment" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("clientsPanel.segmentsAll")}</SelectItem>
              {segments.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        <p className="text-sm text-muted-foreground" data-testid="clients-count">{filtered.length} {t("clientsPanel.clientsCount")}</p>
      </div>

      <div className="rounded-2xl border border-border bg-card overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-xs uppercase tracking-[0.16em] text-muted-foreground bg-muted/40">
              <th className="text-left font-semibold px-6 py-3">{t("clientsPanel.colClient")}</th>
              <th className="text-left font-semibold px-6 py-3">{t("clientsPanel.colStatus")}</th>
              <th className="text-left font-semibold px-6 py-3">{t("clientsPanel.colTags")}</th>
              <th className="text-left font-semibold px-6 py-3">{t("clientsPanel.colLastVisit")}</th>
              <th className="text-left font-semibold px-6 py-3">{t("clientsPanel.colPhone")}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border" data-testid="clients-table-body">
            {filtered.map((c) => (
              <tr key={c.id} className="hover:bg-muted/40 transition-colors cursor-pointer" onClick={() => openClient(c)} data-testid={`client-row-${c.id}`}>
                <td className="px-6 py-4">
                  <div className="flex items-center gap-3">
                    <div className="h-9 w-9 rounded-full bg-secondary grid place-items-center text-sm font-medium">
                      {c.name.split(" ").map((p) => p[0]).slice(0, 2).join("")}
                    </div>
                    <div>
                      <p className="font-medium">{c.name}</p>
                      <p className="text-xs text-muted-foreground">{c.email}</p>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4">
                  <span className={`text-xs px-2.5 py-1 rounded-full capitalize ${statusStyle[c.status] ?? ""}`}>{c.status}</span>
                </td>
                <td className="px-6 py-4">
                  <div className="flex flex-wrap gap-1">
                    {c.tags?.map((tag) => (
                      <span key={tag} className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border border-border text-muted-foreground">{tag}</span>
                    ))}
                  </div>
                </td>
                <td className="px-6 py-4 text-muted-foreground">{formatDate(c.last_visit)}</td>
                <td className="px-6 py-4 font-mono text-xs text-muted-foreground">{c.phone}</td>
              </tr>
            ))}
            {!filtered.length && (
              <tr><td colSpan={5} className="px-6 py-12 text-center text-muted-foreground">{t("clientsPanel.emptyList")}</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <Sheet open={!!selected} onOpenChange={(o) => !o && closeSheet()}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto" data-testid="client-detail-sheet">
          {selected && (
            <>
              <SheetHeader>
                <div className="flex items-center gap-3">
                  <div className="h-12 w-12 rounded-full bg-secondary grid place-items-center font-medium">
                    {(profileForm.name || selected.name).split(" ").map((p) => p[0]).slice(0, 2).join("")}
                  </div>
                  <div>
                    <SheetTitle className="font-display text-2xl tracking-tight">{profileForm.name || selected.name}</SheetTitle>
                    <SheetDescription>{profileForm.email || selected.email}</SheetDescription>
                    {canPatient360 && (
                      <button
                        type="button"
                        className="text-xs text-accent underline mt-1"
                        onClick={() => setSheetTab("clinic")}
                      >
                        {t("clientsPanel.open360Tabs")}
                      </button>
                    )}
                  </div>
                </div>
              </SheetHeader>

              <div className="mt-6 space-y-6">
                <div className="rounded-xl border border-border p-4 space-y-3">
                  <p className="text-xs uppercase tracking-[0.18em] font-semibold text-muted-foreground">{t("clientsPanel.profileSection")}</p>
                  <div>
                    <Label className="text-xs">{t("clientsPanel.fieldName")}</Label>
                    <Input className="h-8 mt-0.5 text-sm" value={profileForm.name} onChange={(e) => setProfileForm((f) => ({ ...f, name: e.target.value }))} />
                  </div>
                  <div>
                    <Label className="text-xs">{t("clientsPanel.fieldEmail")}</Label>
                    <Input className="h-8 mt-0.5 text-sm" value={profileForm.email} onChange={(e) => setProfileForm((f) => ({ ...f, email: e.target.value }))} />
                  </div>
                  <div>
                    <Label className="text-xs">{t("clientsPanel.fieldPhone")}</Label>
                    <Input className="h-8 mt-0.5 text-sm" value={profileForm.phone} onChange={(e) => setProfileForm((f) => ({ ...f, phone: e.target.value }))} />
                  </div>
                  <div>
                    <Label className="text-xs">{t("clientsPanel.fieldStatus")}</Label>
                    <select
                      className="mt-0.5 h-8 w-full rounded-md border border-input bg-background px-2 text-sm capitalize"
                      value={profileForm.status}
                      onChange={(e) => setProfileForm((f) => ({ ...f, status: e.target.value }))}
                    >
                      <option value="active">active</option>
                      <option value="new">new</option>
                      <option value="inactive">inactive</option>
                    </select>
                  </div>
                  <div>
                    <Label className="text-xs">{t("clientsPanel.fieldTags")}</Label>
                    <Input className="h-8 mt-0.5 text-sm" value={profileForm.tags} onChange={(e) => setProfileForm((f) => ({ ...f, tags: e.target.value }))} placeholder={t("clientsPanel.tagsHint")} />
                  </div>
                  <div>
                    <Label className="text-xs">{t("clientsPanel.fieldNote")}</Label>
                    <Input className="h-8 mt-0.5 text-sm" value={profileForm.note} onChange={(e) => setProfileForm((f) => ({ ...f, note: e.target.value }))} />
                  </div>
                  <p className="text-xs text-muted-foreground">{t("clientsPanel.lastVisit")}: {formatDate(selected.last_visit)}</p>
                  <Button type="button" size="sm" className="rounded-full w-full" onClick={saveProfile}>
                    {t("clientsPanel.saveProfile")}
                  </Button>
                </div>

                {canPatient360 && glance && (
                  <div className="rounded-xl border border-border p-4 space-y-2" data-testid="client-glance">
                    <p className="text-xs uppercase tracking-[0.18em] font-semibold text-muted-foreground">{t("clientsPanel.glanceTitle")}</p>
                    <div className="grid gap-2 text-sm">
                      <Link to={`/app/appointments?client=${selected.id}&action=new`} className="flex justify-between rounded-lg border border-border px-3 py-2 hover:bg-muted/40">
                        <span>{t("clientsPanel.glanceAppt")}</span>
                        <span className="text-muted-foreground text-xs">
                          {glance.lastAppt ? `${glance.lastAppt.type} · ${formatDate(glance.lastAppt.start)}` : "—"}
                        </span>
                      </Link>
                      <Link to={`/app/notes?client=${selected.id}`} className="flex justify-between rounded-lg border border-border px-3 py-2 hover:bg-muted/40">
                        <span>{t("clientsPanel.glanceNotes")}</span>
                        <span className="text-muted-foreground text-xs">
                          {glance.notesCount}{glance.draftNotes ? ` (${glance.draftNotes} draft)` : ""}
                        </span>
                      </Link>
                      <Link
                        to={
                          glance.activePlan
                            ? `/app/care-plans?client=${selected.id}&plan=${glance.activePlan.id}`
                            : `/app/care-plans?client=${selected.id}`
                        }
                        className="flex justify-between rounded-lg border border-border px-3 py-2 hover:bg-muted/40"
                      >
                        <span>{t("clientsPanel.glanceCare")}</span>
                        <span className="text-muted-foreground text-xs truncate max-w-[50%]">
                          {glance.activePlan ? `${glance.activePlan.title} (${glance.activePlan.status})` : "—"}
                        </span>
                      </Link>
                      <Link to="/app/billing" className="flex justify-between rounded-lg border border-border px-3 py-2 hover:bg-muted/40">
                        <span>{t("clientsPanel.glanceUnpaid")}</span>
                        <span className="text-muted-foreground text-xs">{glance.unpaidCount}</span>
                      </Link>
                    </div>
                    <div className="flex flex-wrap gap-2 pt-1">
                      {canWaitlist && (
                        <Button type="button" variant="outline" size="sm" className="rounded-full gap-1" onClick={addToWaitlist}>
                          <Clock className="h-3.5 w-3.5" /> {t("clientsPanel.addWaitlist")}
                        </Button>
                      )}
                      {hasReferralBilet && (
                        <Button type="button" variant="outline" size="sm" className="rounded-full" onClick={() => navigate("/app/referrals")}>
                          {t("clientsPanel.openReferrals")}
                        </Button>
                      )}
                    </div>
                  </div>
                )}

                <div className="rounded-xl border border-border p-4 space-y-3">
                  <Button
                    type="button"
                    variant="secondary"
                    className="rounded-full w-full gap-2"
                    onClick={() => navigate(`/app/care-plans?client=${selected.id}`)}
                  >
                    <ClipboardList className="h-4 w-4" />
                    {t("clientsPanel.carePlans")}
                  </Button>
                  {enabledVerticals.length > 1 && (
                    <div>
                      <Label className="text-xs">{t("clientsPanel.clientTrack")}</Label>
                      <select
                        className="mt-1 h-8 w-full rounded-md border border-input bg-background px-2 text-sm"
                        value={clientTrack}
                        onChange={(e) => setClientTrack(e.target.value)}
                      >
                        <option value="">{t("clientsPanel.allSections")}</option>
                        {CLINICAL_VERTICALS.filter((v) => enabledVerticals.includes(v.value)).map((v) => (
                          <option key={v.value} value={v.value}>{v.label}</option>
                        ))}
                      </select>
                      <Button type="button" variant="outline" size="sm" className="rounded-full w-full mt-2" onClick={saveClientTrack}>
                        {t("clientsPanel.saveTrack")}
                      </Button>
                    </div>
                  )}
                  {showCnas && (
                  <>
                  <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t("clientsPanel.cnasSection")}</p>
                  <PilotBanner className="my-2">
                    {t("clientsPanel.cnasPilotBanner")}
                  </PilotBanner>
                  <p className="text-[10px] text-muted-foreground leading-snug">{t("clientsPanel.cnasHint")}</p>
                  <div className="grid gap-2">
                    <div>
                      <Label className="text-xs">{t("clientsPanel.cnasInsured")}</Label>
                      <Input className="h-8 mt-0.5 text-sm" value={cnasForm.cnas_insured_code} onChange={(e) => setCnasForm((f) => ({ ...f, cnas_insured_code: e.target.value }))} />
                    </div>
                    <div>
                      <Label className="text-xs">{t("clientsPanel.cnasPatient")}</Label>
                      <Input className="h-8 mt-0.5 text-sm" value={cnasForm.cnas_patient_id} onChange={(e) => setCnasForm((f) => ({ ...f, cnas_patient_id: e.target.value }))} />
                    </div>
                    <div>
                      <Label className="text-xs">{t("clientsPanel.birthDate")}</Label>
                      <Input className="h-8 mt-0.5 text-sm" placeholder="YYYY-MM-DD" value={cnasForm.birth_date} onChange={(e) => setCnasForm((f) => ({ ...f, birth_date: e.target.value }))} />
                    </div>
                    <div>
                      <Label className="text-xs">{t("clientsPanel.parafa")}</Label>
                      <Input className="h-8 mt-0.5 text-sm" value={cnasForm.parafa} onChange={(e) => setCnasForm((f) => ({ ...f, parafa: e.target.value }))} />
                    </div>
                    <div>
                      <Label className="text-xs">{t("clientsPanel.referral")}</Label>
                      <Input className="h-8 mt-0.5 text-sm" value={cnasForm.referral_bilet} onChange={(e) => setCnasForm((f) => ({ ...f, referral_bilet: e.target.value }))} />
                    </div>
                    <Button type="button" variant="outline" size="sm" className="rounded-full w-full" onClick={saveCnasPilotFields}>
                      {t("clientsPanel.saveCnas")}
                    </Button>
                  </div>
                  </>
                  )}
                  {showMsk && (
                    <>
                      <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground pt-2">{t("clientsPanel.mskSection")}</p>
                      <div className="grid gap-2">
                        <div>
                          <Label className="text-xs">{t("clientsPanel.mskDiagnosis")}</Label>
                          <Input className="h-8 mt-0.5 text-sm" value={mskForm.primary_diagnosis} onChange={(e) => setMskForm((f) => ({ ...f, primary_diagnosis: e.target.value }))} />
                        </div>
                        <div>
                          <Label className="text-xs">{t("clientsPanel.mskRegion")}</Label>
                          <Input className="h-8 mt-0.5 text-sm" value={mskForm.affected_region} onChange={(e) => setMskForm((f) => ({ ...f, affected_region: e.target.value }))} />
                        </div>
                        <div>
                          <Label className="text-xs">{t("clientsPanel.mskVas")}</Label>
                          <Input className="h-8 mt-0.5 text-sm" value={mskForm.vas_baseline} onChange={(e) => setMskForm((f) => ({ ...f, vas_baseline: e.target.value }))} />
                        </div>
                        <div>
                          <Label className="text-xs">{t("clientsPanel.mskReferral")}</Label>
                          <Input className="h-8 mt-0.5 text-sm" value={mskForm.referral_bilet_msk} onChange={(e) => setMskForm((f) => ({ ...f, referral_bilet_msk: e.target.value }))} />
                        </div>
                        <Button type="button" variant="outline" size="sm" className="rounded-full w-full" onClick={saveMskFields}>
                          {t("clientsPanel.saveMsk")}
                        </Button>
                      </div>
                    </>
                  )}
                </div>

                {summary && (
                  <div className="rounded-xl bg-secondary/40 p-4 text-sm">
                    <p className="text-xs font-semibold uppercase tracking-wider mb-2">{t("clientsPanel.aiSummary")}</p>
                    <p>{summary.summary}</p>
                    {summary.risk_flags?.length > 0 && (
                      <ul className="mt-2 text-xs text-destructive">
                        {summary.risk_flags.map((f) => <li key={f}>• {f}</li>)}
                      </ul>
                    )}
                  </div>
                )}

                {sentiment.length > 0 && (
                  <div>
                    <p className="text-xs uppercase tracking-[0.18em] font-semibold text-muted-foreground mb-2">{t("clientsPanel.sentimentTrend")}</p>
                    <div className="h-24">
                      <ResponsiveContainer width="100%" height={90}>
                        <LineChart data={sentiment}>
                          <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                          <YAxis domain={[-1, 1]} width={28} tick={{ fontSize: 10 }} />
                          <Tooltip />
                          <Line type="monotone" dataKey="score" stroke="#0d9488" strokeWidth={2} dot />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                )}

                <div>
                  <p className="text-xs uppercase tracking-[0.18em] font-semibold text-muted-foreground mb-2 flex items-center gap-1">
                    <UserPlus className="h-3 w-3" /> {t("clientsPanel.relations")}
                  </p>
                  <ul className="text-xs space-y-1 mb-2">
                    {relations.map((r) => (
                      <li key={r.id} className="flex justify-between items-center gap-2">
                        <span>{r.related_name || r.related_client_id} · {r.relation_type}</span>
                        <button type="button" className="text-muted-foreground hover:text-destructive" onClick={() => removeRelation(r.id)}>×</button>
                      </li>
                    ))}
                    {!relations.length && <li className="text-muted-foreground">{t("clientsPanel.noRelations")}</li>}
                  </ul>
                  <div className="grid gap-2">
                    <select
                      className="h-8 w-full rounded-md border border-input bg-background px-2 text-sm"
                      value={relationForm.related_client_id}
                      onChange={(e) => setRelationForm((f) => ({ ...f, related_client_id: e.target.value }))}
                    >
                      <option value="">{t("clientsPanel.pickClient")}</option>
                      {otherClients.map((c) => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                      ))}
                    </select>
                    <select
                      className="h-8 w-full rounded-md border border-input bg-background px-2 text-sm"
                      value={relationForm.relation_type}
                      onChange={(e) => setRelationForm((f) => ({ ...f, relation_type: e.target.value }))}
                    >
                      <option value="family">{t("clientsPanel.relFamily")}</option>
                      <option value="partner">{t("clientsPanel.relPartner")}</option>
                      <option value="emergency">{t("clientsPanel.relEmergency")}</option>
                      <option value="guardian">{t("clientsPanel.relGuardian")}</option>
                    </select>
                    <Button type="button" variant="outline" size="sm" className="rounded-full w-full" onClick={addRelation} disabled={!relationForm.related_client_id}>
                      {t("clientsPanel.addRelation")}
                    </Button>
                  </div>
                </div>

                {showPsych && (
                <div>
                  <p className="text-xs uppercase tracking-[0.18em] font-semibold text-muted-foreground mb-2">PHQ-9</p>
                {assessments.length > 0 ? (
                    <>
                    <div className="h-32">
                      <ResponsiveContainer width="100%" height={120}>
                        <LineChart data={[...assessments].reverse().map((a) => ({ date: a.recorded_at?.slice(0, 10), score: a.score }))}>
                          <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                          <YAxis domain={[0, 27]} width={24} tick={{ fontSize: 10 }} />
                          <Tooltip />
                          <Line type="monotone" dataKey="score" stroke="hsl(var(--accent))" strokeWidth={2} dot />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                      <Button variant="ghost" size="sm" className="mt-1" onClick={() => setPhqOpen(true)}>+ {t("clientsPanel.addPhq")}</Button>
                    </>
                  ) : (
                    <Button variant="ghost" size="sm" onClick={() => setPhqOpen(true)}>{t("clientsPanel.addPhq")}</Button>
                  )}
                </div>
                )}

                {attachments.length > 0 && (
                  <div>
                    <p className="text-xs uppercase tracking-[0.18em] font-semibold text-muted-foreground mb-2 flex items-center gap-1">
                      <Paperclip className="h-3 w-3" /> {t("clientsPanel.attachments")}
                    </p>
                    <ul className="text-xs space-y-1">
                      {attachments.map((a) => <li key={a.id}>{a.filename}</li>)}
                    </ul>
                  </div>
                )}
                <label className="inline-flex items-center gap-2 text-sm cursor-pointer text-muted-foreground hover:text-foreground">
                  <input type="file" className="hidden" onChange={(e) => e.target.files?.[0] && uploadAttachment(e.target.files[0])} />
                  <Upload className="h-4 w-4" /> {t("clientsPanel.attachFile")}
                </label>

                {canPatient360 ? (
                  <Tabs value={sheetTab} onValueChange={setSheetTab} className="w-full">
                    <TabsList className="rounded-full w-full flex flex-wrap h-auto gap-1">
                      <TabsTrigger value="activitate" className="rounded-full text-xs">{t("clientsPanel.tabActivity")}</TabsTrigger>
                      <TabsTrigger value="clinic" className="rounded-full text-xs">{t("clientsPanel.tabClinic")}</TabsTrigger>
                      <TabsTrigger value="facturare" className="rounded-full text-xs">{t("clientsPanel.tabBilling")}</TabsTrigger>
                      <TabsTrigger value="comunicari" className="rounded-full text-xs">{t("clientsPanel.tabComms")}</TabsTrigger>
                    </TabsList>
                    <TabsContent value="activitate" className="mt-4">
                      <TimelineList items={timeline} hrefFor={timelineHref} emptyHint={t("clientsPanel.timelineEmpty")} />
                    </TabsContent>
                    <TabsContent value="clinic" className="mt-4 space-y-2 text-sm">
                      {(overview?.notes || []).map((n) => (
                        <Link key={n.id} to={`/app/notes?client=${selected.id}`} className="block rounded-lg border border-border px-3 py-2 hover:bg-muted/40">
                          {n.type} — {formatDate(n.created_at)} ({n.status})
                        </Link>
                      ))}
                      {(overview?.care_plans || []).map((p) => (
                        <Link
                          key={p.id}
                          to={`/app/care-plans?client=${selected.id}&plan=${p.id}`}
                          className="block rounded-lg border border-border px-3 py-2 hover:bg-muted/40"
                        >
                          {p.title} ({p.modality}) — {p.status}
                        </Link>
                      ))}
                      {!overview?.notes?.length && !overview?.care_plans?.length && (
                        <p className="text-muted-foreground text-xs">{t("clientsPanel.noClinical")}</p>
                      )}
                    </TabsContent>
                    <TabsContent value="facturare" className="mt-4 space-y-2 text-sm">
                      {(overview?.invoices || []).map((inv) => (
                        <Link key={inv.id} to="/app/billing" className="flex justify-between rounded-lg border border-border px-3 py-2 hover:bg-muted/40">
                          <span className="font-mono text-xs">{inv.number}</span>
                          <span>{formatRon(inv.amount)}</span>
                          <span className="capitalize text-muted-foreground">{inv.status}</span>
                        </Link>
                      ))}
                      {!overview?.invoices?.length && (
                        <p className="text-muted-foreground text-xs">{t("clientsPanel.noInvoices")}</p>
                      )}
                    </TabsContent>
                    <TabsContent value="comunicari" className="mt-4 space-y-2 text-sm">
                      {(overview?.communications || []).map((e) => (
                        <Link key={e.id} to="/app/communications" className="block rounded-lg border border-border px-3 py-2 hover:bg-muted/40">
                          [{e.channel}] {e.subject || (e.body || "").slice(0, 60)} — {formatDate(e.created_at)}
                        </Link>
                      ))}
                      {!overview?.communications?.length && (
                        <p className="text-muted-foreground text-xs">{t("clientsPanel.noComms")}</p>
                      )}
                    </TabsContent>
                  </Tabs>
                ) : (
                  <div>
                    <p className="text-xs uppercase tracking-[0.18em] font-semibold text-muted-foreground mb-3">{t("clientsPanel.timeline")}</p>
                    <TimelineList items={timeline} hrefFor={timelineHref} emptyHint={t("clientsPanel.timelineEmpty")} />
                  </div>
                )}
              </div>

              <div className="mt-8 flex gap-2 flex-wrap">
                <Button className="rounded-full flex-1" onClick={() => navigate(`/app/notes?client=${selected.id}`)}>{t("clientsPanel.openChart")}</Button>
                <Button variant="outline" className="rounded-full" onClick={() => navigate(`/app/appointments?client=${selected.id}&action=new`)}>{t("clientsPanel.book")}</Button>
                <Button variant="outline" className="rounded-full gap-1" onClick={issuePortalLink} title={t("clientsPanel.portal")}>
                  <Link2 className="h-4 w-4" /> {t("clientsPanel.portal")}
                </Button>
                <Button variant="outline" size="sm" className="rounded-full" onClick={aiSummary}>
                  <Sparkles className="h-4 w-4" />
                </Button>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{t("clientsPanel.newClient")}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>{t("clientsPanel.fieldName")}</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div><Label>{t("clientsPanel.fieldEmail")}</Label><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
            <div><Label>{t("clientsPanel.fieldPhone")}</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
            <div><Label>{t("clientsPanel.fieldTags")}</Label><Input value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} /></div>
          </div>
          <Button className="w-full rounded-full mt-4" onClick={addClient}>{t("clientsPanel.save")}</Button>
        </DialogContent>
      </Dialog>

      <Dialog open={phqOpen} onOpenChange={setPhqOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{t("clientsPanel.phqTitle")}</DialogTitle></DialogHeader>
          <div>
            <Label>{t("clientsPanel.phqScore")}</Label>
            <Input
              type="number"
              min={0}
              max={27}
              value={phqScore}
              onChange={(e) => setPhqScore(e.target.value)}
              className="mt-1"
            />
          </div>
          <Button className="w-full rounded-full mt-4" onClick={addAssessment}>{t("clientsPanel.save")}</Button>
        </DialogContent>
      </Dialog>
    </AppLayout>
  )
}

function TimelineList({ items, hrefFor, emptyHint }) {
  return (
    <ul className="space-y-3 text-sm">
      {items.map((item) => {
        const href = hrefFor?.(item)
        const body = (
          <>
            <span className="mt-2 h-1 w-1 rounded-full bg-accent shrink-0" />
            <div>
              <p className="font-medium">{item.title}</p>
              <p className="text-xs text-muted-foreground">{formatDate(item.at)}{item.subtitle ? ` · ${item.subtitle}` : ""}</p>
            </div>
          </>
        )
        return (
          <li key={`${item.type}-${item.id}`}>
            {href ? (
              <Link to={href} className="flex gap-3 hover:opacity-80">{body}</Link>
            ) : (
              <div className="flex gap-3">{body}</div>
            )}
          </li>
        )
      })}
      {!items.length && <li className="text-muted-foreground text-xs">{emptyHint}</li>}
    </ul>
  )
}
